import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  Policy as PolicyIcon,
} from '@material-ui/icons';

import BaseButton from '../common/BaseButton';
import StatusIcon from '../common/StatusIcon';

import {
  selectGuest,
  selectReservation,
  selectNameAddressMatchDemo,
  getGuestById,
  getNameAddressMatchByGuestId,
  createNameAddressMatch,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: 550,
    maxWidth: '100%',
  },
  table: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    width: `calc(100% - ${theme.spacing(8)}px)`,
  },
  row: {
    justifyContent: 'space-between',
  },
  cell: {
    padding: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    'tr:first-child &': {
      borderTop: '1px solid rgba(224, 224, 224, 1)',
    },

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  panel: {
    backgroundColor: theme.palette.background.default,
    minWidth: 550,
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    alignSelf: 'top',
  },
  cardHeading: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    textAlign: 'center',
  },
  equalHeight: {
    height: '100%',
  },
  ratingBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  numberOfReviews: {
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
  },
  ratingBars: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    paddingBottom: theme.spacing(1),
  },
  tableIcon: {
    verticalAlign: 'middle',
  },
}));

function MatchTooltip({ guest, match, children }) {
  return (
    <Tooltip
      title={(
        <>
          <Typography component="p" variant="body2">
            {`Guest Input: ${guest}`}
          </Typography>
          <Typography component="p" variant="body2">
            {`What We Found: ${match}`}
          </Typography>
        </>
      )}
      disableFocusListener={!match}
      disableHoverListener={!match}
      disableTouchListener={!match}
      placement="right-start"
      interactive
    >
      <div>{children}</div>
    </Tooltip>
  );
}

export default function VerifyModal() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const reservation = useSelector(selectReservation);
  const session = useSelector(selectNameAddressMatchDemo);

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && guest && guest.id && reservation && reservation.companyCode && reservation.reservationCode) {
      dispatch(getNameAddressMatchByGuestId({ guestId: guest.id }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Match information retrieved', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Retrieving match information. Please wait...', severity: 'info' }));
          dispatch(createNameAddressMatch({
            companyCode: reservation.companyCode,
            reservationCode: reservation.reservationCode,
          }))
            .unwrap()
            .then(() => {
              dispatch(getGuestById({ guestId: guest.id }));
              dispatch(setMessage({ message: 'Match information found', severity: 'success' }));
            })
            .catch(() => {
              dispatch(setMessage({ message: 'Match information not found', severity: 'error' }));
            });
        });
    }
  }, [dispatch, guest, reservation, open]);

  return (
    <div>
      <BaseButton
        IconComponent={PolicyIcon}
        onClick={handleClickOpen}
        fullWidth
      >
        Address Check
      </BaseButton>
      <Dialog className={classes.dialog} open={open} onClose={handleClose} maxWidth="sm">
        <DialogContent>
          <TableContainer className={classes.container} component={Paper}>
            <Typography className={classes.heading} component="h3">Full Name</Typography>
            <Table className={classes.table}>
              <TableBody>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">First Name</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <StatusIcon
                      className={classes.tableIcon}
                      status={session && session.firstNameVerified && session.firstNameVerified.status}
                    />
                  </TableCell>
                </TableRow>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">Last Name</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <StatusIcon
                      className={classes.tableIcon}
                      status={session && session.lastNameVerified && session.lastNameVerified.status}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer className={classes.container} component={Paper}>
            <Typography className={classes.heading} component="h3">Address</Typography>
            <Table className={classes.table} dense>
              <TableBody>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">Street</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <StatusIcon
                      className={classes.tableIcon}
                      status={session && session.streetVerified && session.streetVerified.status}
                    />
                  </TableCell>
                </TableRow>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">City</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <MatchTooltip
                      guest={session && session.cityVerified && session.cityVerified.guest}
                      match={session && session.cityVerified && session.cityVerified.match}
                    >
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.cityVerified && session.cityVerified.status}
                      />
                    </MatchTooltip>
                  </TableCell>
                </TableRow>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">State</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <MatchTooltip
                      guest={session && session.stateVerified && session.stateVerified.guest}
                      match={session && session.stateVerified && session.stateVerified.match}
                    >
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.stateVerified && session.stateVerified.status}
                      />
                    </MatchTooltip>
                  </TableCell>
                </TableRow>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">Zip/Postal</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <MatchTooltip
                      guest={session && session.postalCodeVerified && session.postalCodeVerified.guest}
                      match={session && session.postalCodeVerified && session.postalCodeVerified.match}
                    >
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.postalCodeVerified && session.postalCodeVerified.status}
                      />
                    </MatchTooltip>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer className={classes.container} component={Paper}>
            <Typography className={classes.heading} component="h3">Contact</Typography>
            <Table className={classes.table}>
              <TableBody>
                <TableRow className={classes.row}>
                  <TableCell className={classes.cell} align="left">Phone</TableCell>
                  <TableCell className={classes.cell} align="right">
                    <MatchTooltip
                      guest={session && session.phoneNumberVerified && session.phoneNumberVerified.guest}
                      match={session && session.phoneNumberVerified && session.phoneNumberVerified.match}
                    >
                      <StatusIcon
                        className={classes.tableIcon}
                        status={session && session.phoneNumberVerified && session.phoneNumberVerified.status}
                      />
                    </MatchTooltip>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
}
